/**
 * @file   src\components\OrderAddDevice.tsx
 * @brief  OrderAddDevice component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../utils/thirdpartyComponents';
import Deviceicon from '../assets/images/computer-chip.svg';
import CWInput from '../components/CWInput';
import MinusIcon from '../assets/images/icons/mine';
import PlusIcon from '../assets/images/icons/plus';
import axios from '../config/axiosConfig';
import { OrderHardwareApi } from '../utils/apiUrls';
import { IDeviceData } from '../interfaces/hardwareInterface';
import SmallLoader from './SmallLoader';

interface IOrderAddDeviceProps {
  onSelectionChange: (cart: { [key: number]: IDeviceData }) => void;
  cartData: any;
}

const OrderAddDevice: React.FC<IOrderAddDeviceProps> = ({ cartData, onSelectionChange }) => {
  const [hardwareList, setHardwareList] = useState<any[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: IDeviceData }>({});
  const [cart, setCart] = useState<{ [key: number]: IDeviceData }>(cartData.devices);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getHardwareList();
  }, []);

  // Function to fetch hardware list
  const getHardwareList = async () => {
    try {
      setLoading(true); // Show loader
      const { data } = await axios.get(OrderHardwareApi.CARWASH_OWNER_LIST_HARDWARE);
      if (data.error === 0) {
      setHardwareList(data.data);

      // Initialize quantities with product information, including 'price'
      const initialQuantities = data.data.reduce((acc: { [key: number]: IDeviceData }, product: any) => {
        acc[product.hardwareId] = {
          quantity: 1,
          hardwareName: product.hardwareName,
          description: product.description,
          image: product.image,
          price: product.price,
          hardwareId: product.hardwareId,
        };
        return acc;
      }, {});

      setQuantities(initialQuantities);
    }
    } catch (error: any) {
      console.error('Error fetching hardware list:', error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
    finally{
      setLoading(false);
    }
  };

  const handleIncrease = (productId: number) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productId]: {
        ...prevQuantities[productId],
        quantity: prevQuantities[productId].quantity + 1,
      },
    }));
  };

  const handleDecrease = (productId: number) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productId]: {
        ...prevQuantities[productId],
        quantity: Math.max(prevQuantities[productId].quantity - 1, 1),
      },
    }));
  };

  const addToCart = (productId: number) => {
    setCart((prevCart) => {
      const updatedCart = {
        ...prevCart,
        [productId]: quantities[productId], // Add entire product details from quantities
      };
      onSelectionChange(updatedCart); // Pass updated cart to parent
      return updatedCart;
    });
  };

  return (
    <div className="device-listing-wrap d-flex scroll-custom">
      {hardwareList.map((product: any) => (
        <div key={product.hardwareId} className="product-card text-center">
          <div className={`device-frame ${product.image ? 'device-img' : ''} d-flex align-items-center justify-content-center`}>
            <img src={product.image ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH || ""}${product.image}` : Deviceicon} alt="Device Icon" />
          </div>
          <h5 className="f-bold">{quantities[product.hardwareId]?.hardwareName}</h5>
          <h2 className="f-bold">${product.price}</h2>
          <p className="f-medium">{quantities[product.hardwareId]?.description}</p>
          <div className="add-input-group mx-auto d-flex align-items-center">
            <Button
              variant="light"
              className="light-btn action-btn"
              onClick={() => handleDecrease(product.hardwareId)}
              disabled={!!cart[product.hardwareId]} // Disable when in cart
            >
              <MinusIcon />
            </Button>
            <CWInput
              id={`addinput-${product.hardwareId}`}
              name={`addinput-${product.hardwareId}`}
              type="text"
              value={quantities[product.hardwareId]?.quantity || 1}
              readOnly
            />
            <Button
              variant="light"
              className="light-btn action-btn"
              onClick={() => handleIncrease(product.hardwareId)}
              disabled={!!cart[product.hardwareId]} // Disable when in cart
            >
              <PlusIcon />
            </Button>
          </div>
          <Button
            variant="primary"
            className={"primary-btn"}
            onClick={() => addToCart(product.hardwareId)}
            disabled={!!cart[product.hardwareId]} // Disable when already in cart
            title={cart[product.hardwareId] ? "Added" : "Add To Cart"}
          >
            {cart[product.hardwareId] ? "Added" : "Add To Cart"}
          </Button>
        </div>
      ))}
          {loading && <SmallLoader />}
    </div>
  );
};

export default OrderAddDevice;
